import React, { useState } from "react";
import { CornerDownRight, X, Check } from "lucide-react";

export interface TransferItem {
  _id: string;
  serialNumber: string;
  fromWarehouse: { name: string; _id: string };
  toWarehouse?: { name: string; _id: string };
  sentBy: { username: string };
  sentDate: string;
  senderRemark?: string;
  transferCost?: number;
  handlingCost?: number;
  productName?: string;
  productId?: string;
}

interface TransferCardProps {
  transfer: TransferItem;
  type: "incoming" | "outgoing";
  actionLoading: boolean;
  // Handler returns a Promise so we can wait for success
  onConfirmAction: (id: string, remark: string) => Promise<boolean>;
  LoadingSpinner: () => JSX.Element;
}

const TransferCard: React.FC<TransferCardProps> = ({
  transfer,
  type,
  actionLoading,
  onConfirmAction,
  LoadingSpinner,
}) => {
  const isIncoming = type === "incoming";

  // Local state for inline confirmation
  const [isConfirming, setIsConfirming] = useState(false);
  const [remark, setRemark] = useState("");

  const serialColor = isIncoming ? "text-amber-800" : "text-yellow-800";
  const actionColor = isIncoming
    ? "bg-amber-600 hover:bg-amber-700"
    : "bg-red-600 hover:bg-red-700";

  const handleStart = () => {
    setIsConfirming(true);
    setRemark("");
  };

  const handleCancelLocal = () => {
    setIsConfirming(false);
    setRemark("");
  };

  const handleSubmit = async () => {
    const success = await onConfirmAction(transfer._id, remark);
    if (success) {
      setIsConfirming(false);
    }
  };

  const renderAction = () =>
    !isConfirming ? (
      <button
        type="button"
        onClick={handleStart}
        disabled={actionLoading}
        className={`inline-flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-50 ${actionColor}`}
      >
        {isIncoming ? <CornerDownRight size={17} /> : <X size={17} />}
        {isIncoming ? "Accept transfer" : "Cancel transfer"}
      </button>
    ) : (
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          placeholder={
            isIncoming
              ? "Receiver remark (optional)"
              : "Cancellation reason (optional)"
          }
          value={remark}
          onChange={(event) => setRemark(event.target.value)}
          disabled={actionLoading}
          className="min-h-11 min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#0077C0] focus:outline-none focus:ring-2 focus:ring-blue-100"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={actionLoading}
            className={`inline-flex min-h-11 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold text-white disabled:opacity-60 sm:flex-none ${
              isIncoming
                ? "bg-green-700 hover:bg-green-800"
                : "bg-red-700 hover:bg-red-800"
            }`}
          >
            {actionLoading ? <LoadingSpinner /> : <Check size={16} />}
            Confirm
          </button>
          <button
            type="button"
            onClick={handleCancelLocal}
            disabled={actionLoading}
            aria-label="Cancel action"
            className="min-h-11 rounded-lg border border-gray-300 px-3 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    );

  return (
    <>
      <tr className="hidden border-b border-gray-100 transition-colors hover:bg-gray-50 lg:table-row">
        <td className="whitespace-nowrap px-4 py-4">
          <span className={`font-mono text-lg font-bold ${serialColor}`}>
            {transfer.serialNumber}
          </span>
        </td>
        <td className="px-4 py-4 text-sm font-medium text-gray-800">
          {transfer.productName || "—"}
        </td>
        <td className="px-4 py-4 text-sm">
          <div className="font-medium text-gray-900">
            {transfer.fromWarehouse?.name || "—"}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            To {transfer.toWarehouse?.name || "—"}
          </div>
        </td>
        <td className="whitespace-nowrap px-4 py-4 text-sm">
          <div className="font-medium text-gray-800">
            {transfer.sentBy?.username || "—"}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            {new Date(transfer.sentDate).toLocaleString()}
          </div>
        </td>
        <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-700">
          <div>Transfer: ₹{(transfer.transferCost || 0).toLocaleString()}</div>
          <div className="mt-1 text-xs text-gray-500">
            Handling: ₹{(transfer.handlingCost || 0).toLocaleString()}
          </div>
        </td>
        <td className="max-w-xs px-4 py-4 text-sm text-gray-600">
          <span className="block truncate" title={transfer.senderRemark || ""}>
            {transfer.senderRemark || "—"}
          </span>
        </td>
        <td className="min-w-[260px] px-4 py-4">{renderAction()}</td>
      </tr>
      <tr className="border-b border-gray-100 lg:hidden">
        <td colSpan={7} className="p-3">
          <article className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-mono text-base font-bold text-gray-900">
                  {transfer.serialNumber}
                </p>
                <p className="mt-1 truncate text-sm font-medium text-gray-700">
                  {transfer.productName || "Product name unavailable"}
                </p>
              </div>
              <span
                className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
                  isIncoming
                    ? "bg-amber-50 text-amber-800"
                    : "bg-yellow-50 text-yellow-800"
                }`}
              >
                {isIncoming ? "Incoming" : "Outgoing"}
              </span>
            </div>

            <div className="my-4 grid grid-cols-2 gap-x-3 gap-y-4 border-y border-gray-100 py-4 text-sm">
              <div className="col-span-2">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  Warehouse route
                </p>
                <p className="mt-1 font-medium text-gray-800">
                  {transfer.fromWarehouse?.name || "—"}
                  <span className="mx-2 text-gray-400">→</span>
                  {transfer.toWarehouse?.name || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  Sent by
                </p>
                <p className="mt-1 font-medium text-gray-800">
                  {transfer.sentBy?.username || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  Sent date
                </p>
                <p className="mt-1 text-gray-700">
                  {new Date(transfer.sentDate).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  Transfer cost
                </p>
                <p className="mt-1 text-gray-700">
                  ₹{(transfer.transferCost || 0).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  Handling cost
                </p>
                <p className="mt-1 text-gray-700">
                  ₹{(transfer.handlingCost || 0).toLocaleString()}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  Sender remark
                </p>
                <p className="mt-1 break-words text-gray-700">
                  {transfer.senderRemark || "—"}
                </p>
              </div>
            </div>

            {renderAction()}
          </article>
        </td>
      </tr>
    </>
  );
};

export default TransferCard;
